import React from 'react';
import CardsGrid from 'src/sections/Cards/CardsGrid';
import { CmsSectionalHeader } from 'src/sections';
import { RichTextSwitch } from 'src/containers';
import { useCmsMode } from 'src/contexts/ModeContext/ModeContext';
import { useComponentTheme } from 'src/hooks/useComponentTheme';
import ThemeProvider from 'threads5/themes/theme-provider';
import { BackgroundContext } from 'src/contexts';
import { CmsComponents } from 'src/lib/constants';
import { withCtaLinkOverlay } from 'src/styles/mixins/btn-link-overlay-styles';

const CmsCardsGrid = ({ fields, sys }) => {
  const rows = [fields?.gridCardsRow1 ?? [], fields?.gridCardsRow2 ?? []];

  const bgColor = fields?.backgroundColor || 'white';
  const cmsMode = useCmsMode({
    mode: fields?.mode,
    backgroundColor: bgColor,
  });
  const componentTheme = useComponentTheme(cmsMode);
  const background = componentTheme.palette[bgColor] || bgColor;
  const isGradient = fields?.backgroundColor?.includes('gradient');

  return (
    <ThemeProvider theme={componentTheme}>
      <BackgroundContext.Provider value={bgColor}>
        <CardsGrid.Container
          sys={sys}
          sx={{
            background,
            ...fields?.styles,
          }}
          heading={
            <CmsSectionalHeader
              sys={fields?.header?.sys}
              fields={{
                ...fields?.header?.fields,
                backgroundColor: isGradient ? 'transparent' : background,
              }}
            />
          }
          rows={rows.map((row) => {
            return row.map((card, index) => {
              return toGridCard(card, index, cmsMode);
            });
          })}
          mobileLayout={fields?.mobileLayout ?? 'Stacked'}
        />
      </BackgroundContext.Provider>
    </ThemeProvider>
  );
};

export default CmsCardsGrid;

const getShowHolographicBorder = (card, hasCta: boolean) => {
  const cmsValue = card?.fields?.showHolographicBorder;
  // Explicit CMS override takes precedence
  if (cmsValue === true || cmsValue === false) return cmsValue;
  // Default: show the hover border only when the card has a CTA to click
  return hasCta;
};

// Video/Modal buttons are handled specially by EmbeddedEntryButton (play a video,
// open a modal) rather than navigating, so they don't make the card a link. This
// mirrors makeClickHandler in EmbeddedEntryButton: only non-Video/Modal targets
// with a url actually navigate.
const NON_NAVIGATIONAL_TARGETS = ['Video', 'Modal'];

const isNavigationalCta = (target): boolean => {
  return (
    target?.sys?.contentType?.sys?.id === CmsComponents.BUTTON &&
    Boolean(target?.fields?.url) &&
    !NON_NAVIGATIONAL_TARGETS.includes(target?.fields?.target)
  );
};

const getNavigationalButtonsFromEntry = (entry) => {
  const contentType = entry?.sys?.contentType?.sys?.id;

  if (contentType === CmsComponents.BUTTON) {
    return isNavigationalCta(entry) ? [entry] : [];
  }

  if (contentType === CmsComponents.BUTTON_GROUP) {
    return (entry?.fields?.buttons || []).filter((button) => {
      return isNavigationalCta(button);
    });
  }

  return [];
};

const getCtaButtonCount = (content) => {
  if (!Array.isArray(content)) return 0;
  return content.reduce((count, node) => {
    return count + getNavigationalButtonsFromEntry(node?.data?.target).length;
  }, 0);
};

export const toGridCard = (card, index, parentMode) => {
  const body = card?.fields?.standardBody;

  // A grid card becomes a whole-card link only when its body has exactly one
  // navigational button2 CTA. That CTA gets a stretched ::after overlay and the
  // holographic hover border is shown. With zero or multiple CTAs, neither applies.
  const ctaCount = getCtaButtonCount(body?.content);
  const hasSingleCta = ctaCount === 1;

  return (
    <CardsGrid.Card
      className='cms-cards-grid-card'
      sx={{
        ...card?.fields?.styles,
        ...(hasSingleCta && withCtaLinkOverlay()),
      }}
      mode={card?.fields?.mode || parentMode}
      backgroundColor={card?.fields?.backgroundColor || 'transparent'}
      showHolographicBorder={getShowHolographicBorder(card, hasSingleCta)}
      key={card?.sys?.id + index}
    >
      <RichTextSwitch
        sys={card?.sys}
        content={body}
        nodes={{
          h3: {
            component: 'h3',
            fieldId: 'standardBody',
            theme: 'web 3.0',
            variant: 'Subhead SemiBold',
          },
          p: {
            className: '',
            component: 'p',
            fieldId: 'standardBody',
            theme: 'web 3.0',
            variant: 'P2 Regular',
          },
          asset: {
            queryString: {
              xs: '?fm=webp&w=48&q=60',
              sm: '?fm=webp&w=48&q=60',
              md: '?fm=webp&w=48&q=60',
              lg: '?fm=webp&w=48&q=60',
            },
            width: 48,
            height: 48,
          },
        }}
      />
    </CardsGrid.Card>
  );
};
